import React, { useState } from 'react';
import { UserDisplayName } from '@/shared/components/UserDisplayName';
import { getFinalDisplayName } from '@/shared/utils/displayName';

export function CommentItem({ comment, onVote, onReply, currentUser, isReply = false }) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleVote = (voteType) => {
    if (!currentUser) return;
    
    // If user clicks the same vote type, remove their vote (set to 0)
    const newVoteType = comment.userVote === voteType ? 0 : voteType;
    onVote(comment.id, newVoteType);
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!currentUser || !replyContent.trim() || submittingReply) return;

    setSubmittingReply(true);
    try {
      await onReply(comment.id, replyContent.trim());
      setReplyContent('');
      setShowReplyForm(false);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setSubmittingReply(false);
    }
  };

  const netScore = (comment.upvotes || 0) - (comment.downvotes || 0);

  // Get the display name to show
  const displayName = getFinalDisplayName(comment.userEmail, comment.userDisplayName);

  return (
    <div className={`${isReply ? 'ml-6 sm:ml-12' : ''}`}>
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
        {/* Comment Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-2">
          <div className="flex items-center space-x-2">
            <UserDisplayName 
              displayName={displayName}
              className="text-gray-900"
            />
            {isReply && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Reply</span>
            )}
          </div>
          <span className="text-xs sm:text-sm text-gray-500">{formatDate(comment.createdAt)}</span>
        </div>

        {/* Comment Content */}
        <p className="text-gray-700 whitespace-pre-wrap text-sm sm:text-base leading-relaxed mb-4">
          {comment.content}
        </p>

        {/* Comment Actions */}
        <div className="flex items-center justify-between">
          {/* Voting */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleVote(1)}
                disabled={!currentUser}
                className={`flex items-center space-x-1 px-2 py-1 rounded-md transition-colors cursor-pointer ${
                  comment.userVote === 1
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-green-600'
                } ${!currentUser ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                <span className="text-sm font-medium">{comment.upvotes || 0}</span>
              </button>

              <button
                onClick={() => handleVote(-1)}
                disabled={!currentUser}
                className={`flex items-center space-x-1 px-2 py-1 rounded-md transition-colors cursor-pointer ${
                  comment.userVote === -1
                    ? 'bg-red-100 text-red-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-red-600'
                } ${!currentUser ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                <span className="text-sm font-medium">{comment.downvotes || 0}</span>
              </button>

              {/* Net Score */}
              <div className="flex items-center space-x-1 px-2 py-1">
                <span className={`text-sm font-medium ${
                  netScore > 0 ? 'text-green-600' : 
                  netScore < 0 ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  {netScore > 0 ? '+' : ''}{netScore}
                </span>
              </div>
            </div>
          </div>

          {/* Reply Button - Only show for top-level comments */}
          {!isReply && currentUser && (
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer transition-colors"
            >
              Reply
            </button>
          )}
        </div>

        {/* Reply Form */}
        {showReplyForm && currentUser && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <form onSubmit={handleSubmitReply} className="space-y-3">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent box-border text-gray-900 text-sm resize-none"
                maxLength={1000}
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">{replyContent.length}/1000 characters</span>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReplyForm(false);
                      setReplyContent('');
                    }}
                    className="text-gray-600 hover:text-gray-800 text-sm cursor-pointer transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!replyContent.trim() || submittingReply}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium cursor-pointer transition-colors text-sm flex items-center"
                  >
                    {submittingReply ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Replying...
                      </>
                    ) : (
                      'Post Reply'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4 space-y-4">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onVote={onVote}
              onReply={onReply}
              currentUser={currentUser}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
}